import { Element, Component, Prop, h, State, Watch } from '@stencil/core';
import { alert_exclamation_circle, alert_question_circle } from 'pn-design-assets/pn-assets/icons.js';

/**
 * The `pn-tooltip` can display slotted HTML content or a text `string` with the `tooltip` prop.
 *
 * Remember that the component is techincally a `button` element, don't put any HTML content that would be out of place in a regular button
 * (no headlines, other input elements, etc...).
 */
@Component({
  tag: 'pn-tooltip',
  styleUrl: 'pn-tooltip.scss',
})
export class PnTooltip {
  content: HTMLDivElement;
  button: HTMLButtonElement;

  openEvents: string[] = ['mouseover', 'focus'];
  closeEvents: string[] = ['mouseleave', 'blur', 'touchcancel', 'touchleave'];
  eventListeners: string[] = [...this.openEvents, ...this.closeEvents];

  timeout: NodeJS.Timeout;

  @Element() hostElement: HTMLElement;

  @State() downwards: boolean = false;
  @State() open: boolean = false;
  @State() deltaX: string | number = '0';

  /** Apply the warning color and change the icon to `!`. */
  @Prop() warning: boolean = false;
  /** Apply the light color if you use a dark background. */
  @Prop() light: boolean = false;
  /** Overrides the slotted content to create a small tooltip instead. */
  @Prop() tooltip?: string;

  @Watch('open')
  openHandler() {
    if (this.open) this.checkPosition();
    else this.setTransform();
  }

  componentDidLoad() {
    this.initTooltip();
  }

  private setTransform() {
    this.content.style.transform = `scale(${Number(this.open)}) translateX(-50%) translateX(${this.deltaX}px)`;
  }

  private getSymbol(): string {
    return this.warning ? alert_exclamation_circle : alert_question_circle;
  }

  private checkPosition() {
    this.content.style.transition = 'none';
    this.content.style.transform = 'scale(1) translateX(-50%)';
    this.downwards = false;

    requestAnimationFrame(() => {
      const { x, y, right } = this.content.getBoundingClientRect();
      const extraMargin = 8;
      this.deltaX = '0';
      this.downwards = y < 0;
      this.content.style.transform = '';

      if (x < 0) this.deltaX = `${x * -1 + extraMargin}`; // left edge
      if (right > window.innerWidth) this.deltaX = `${window.innerWidth - right - extraMargin}`; // right edge

      requestAnimationFrame(() => {
        this.content.style.transition = '';
        this.setTransform();
      });
    });
  }

  private initTooltip() {
    this.eventListeners.forEach(name =>
      this.button.addEventListener(name, (event: MouseEvent | FocusEvent) => this.toggleTooltip(event)),
    );
  }

  private toggleTooltip(event: MouseEvent | FocusEvent) {
    const status = this.openEvents.includes(event.type);

    let time = 500;

    if (status) time = 0;

    clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      this.open = status;
    }, time);
  }

  render() {
    return (
      <button
        class="pn-tooltip"
        data-light={this.light}
        data-warning={this.warning}
        data-downwards={this.downwards}
        data-open={this.open}
        ref={el => (this.button = el)}
      >
        <pn-icon icon={this.getSymbol()} data-simple={!!this.tooltip} />
        <div class="pn-tooltip-content" data-simple={!!this.tooltip} ref={el => (this.content = el)}>
          {this.tooltip}
          <div hidden={!!this.tooltip}>
            <slot />
          </div>
        </div>
      </button>
    );
  }
}
